// ============================================================
// ELC Shift — Uploads module / shared helpers
//
// Parsing primitives and "Did you mean...?" UI used by every
// upload flow (budget, daily, addon). Imported by budget.js,
// daily.js, addon.js via the entry point in src/uploads/index.js.
//
// Free globals referenced as bare identifiers (not window.foo)
// because they live as lexical globals in admin.html (declared
// via `const` / `let` / `function`). Top-level `const`/`let`
// in classic scripts create lexical bindings that ARE visible
// by name from any script but ARE NOT properties of `window`.
// esbuild leaves bare references as free variable lookups at
// runtime, which resolve via the global lexical scope.
// Matches the convention used by src/top50/.
//   - escapeHtml      (admin.html escape helper)
//
// v94.0: storeMatchScore moved out of admin.html into ./match.js;
// imported below rather than referenced as a free global.
// ============================================================

import { storeMatchScore } from './match.js';

export const STORE_SUGGESTION_MIN_SCORE = 0.7;

// Parse a spreadsheet's "matrix" shape: dates down the rows,
// stores across the columns. Returns a {store: {date: value}}
// nested object plus diagnostics for the "Did you mean…?" UI.
//
// Args:
//   rows            — XLSX.utils.sheet_to_json output (2D array)
//   storeMapUpper   — UPPERCASE-keyed map of spreadsheetHeader → appStoreName
//   dateColLabel    — column name to find date column ('DATE TY' etc)
//   extraStoreCols  — optional [{idx, appName}] for user-accepted suggestions
export function parseMatrixSheet(rows, storeMapUpper, dateColLabel, extraStoreCols) {
  // Header row contains dateColLabel + store names. Search top 5 rows.
  let headerRow = -1;
  for (let i = 0; i < Math.min(5, rows.length); i++) {
    const r = rows[i];
    if (r && r.some(c => String(c||'').trim().toUpperCase() === dateColLabel.toUpperCase())) {
      headerRow = i; break;
    }
  }
  if (headerRow === -1) {
    return { ok: false, error: `Could not find header row with "${dateColLabel}" column.` };
  }

  const headers = rows[headerRow];

  // Find the date column index by exact label match (case-insensitive).
  let dateColIdx = -1;
  headers.forEach((h, i) => {
    if (String(h||'').trim().toUpperCase() === dateColLabel.toUpperCase()) dateColIdx = i;
  });
  if (dateColIdx === -1) {
    return { ok: false, error: `"${dateColLabel}" column not found in header row.` };
  }

  // Find store columns by upload-name match.
  const storeCols = [];
  const matchedIdx = new Set();
  headers.forEach((h, i) => {
    const key = String(h||'').trim().toUpperCase();
    const appName = storeMapUpper[key];
    if (appName) {
      storeCols.push({ idx: i, appName });
      matchedIdx.add(i);
    }
  });

  // Merge extra (user-accepted) mappings on top.
  if (Array.isArray(extraStoreCols)) {
    extraStoreCols.forEach(e => {
      if (!matchedIdx.has(e.idx)) {
        storeCols.push({ idx: e.idx, appName: e.appName });
        matchedIdx.add(e.idx);
      }
    });
  }

  // Track unmatched headers for the suggestion UI. Anything that isn't the
  // date column, isn't already matched, and has a non-empty header.
  const unmatchedHeaders = [];
  headers.forEach((h, i) => {
    if (i === dateColIdx) return;
    if (matchedIdx.has(i)) return;
    const label = String(h || '').trim();
    if (!label) return;
    // Skip non-store metadata columns ("Day", "Month", "Week", "UK Month" etc).
    const upper = label.toUpperCase();
    if (['DAY','MONTH','WEEK','UK MONTH','UK WEEK','DATE LY','DATE TY'].includes(upper)) return;
    unmatchedHeaders.push({ idx: i, header: label });
  });

  if (!storeCols.length) {
    return { ok: false, error: `No recognised store columns in header row. Check Stores tab → Upload Names match the spreadsheet headers.`, unmatchedHeaders, headerRow, dateColIdx };
  }

  // Iterate data rows.
  const byStoreDate = {};
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || !row[dateColIdx]) continue;
    let dateStr = String(row[dateColIdx]).trim();
    // Normalise date — handle DD/MM/YYYY and trim trailing time
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        dateStr = parts[2] + '-' + parts[1].padStart(2,'0') + '-' + parts[0].padStart(2,'0');
      }
    } else if (dateStr.length > 10) {
      dateStr = dateStr.slice(0, 10);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) continue;

    storeCols.forEach(sc => {
      const raw = row[sc.idx];
      // Skip empty cells (future dates / missing data).
      if (raw === undefined || raw === null || raw === '') return;
      // v80.16: Coerce "dash-only" text strings to 0. Some upload templates
      // use "-" or " -   " as a placeholder for "none done" — most commonly
      // in the Demo sheet, where stores write a dash rather than 0 on days
      // they ran no demos. parseFloat("-") returns NaN, so without this
      // coercion those days appear as missing data ("–/2") instead of 0/2.
      // Detected: 1751 such cells across 28 stores in the Demo sheet alone.
      // Matches: '-', ' -', ' - ', '--', '—', ' — ', and any whitespace
      // around any combo of ASCII hyphens / en-dashes / em-dashes. Anything
      // with real content (e.g. '- 5') falls through to parseFloat unchanged.
      // Whitespace-only strings (' ') do NOT match — they fall through and
      // get skipped as before, preserving "missing" semantics.
      if (typeof raw === 'string' && /[-\u2013\u2014]/.test(raw) && /^[\s\-\u2013\u2014]+$/.test(raw)) {
        if (!byStoreDate[sc.appName]) byStoreDate[sc.appName] = {};
        byStoreDate[sc.appName][dateStr] = 0;
        return;
      }
      const v = parseFloat(raw);
      if (isNaN(v)) return;
      if (!byStoreDate[sc.appName]) byStoreDate[sc.appName] = {};
      byStoreDate[sc.appName][dateStr] = v;
    });
  }

  return { ok: true, byStoreDate, unmatchedHeaders, headerRow, dateColIdx };
}

// "Did you mean...?" suggestion engine. Given the headers a parse could
// not match (unmatchedHeaders) and the stores that didn't show up
// (missingStores), return a ranked list of best (store, header) pairs
// where the fuzzy match score is above threshold. Greedy assignment so
// each header suggests at most one store and each store gets at most one
// suggestion.
export function findStoreColumnSuggestions(unmatchedHeaders, missingStores, dismissedSet) {
  const candidates = [];
  for (const store of (missingStores || [])) {
    if (dismissedSet && dismissedSet.has(store)) continue;
    for (const h of (unmatchedHeaders || [])) {
      const { score } = storeMatchScore(store, h.header);
      if (score >= STORE_SUGGESTION_MIN_SCORE) {
        candidates.push({ store, header: h.header, idx: h.idx, score });
      }
    }
  }
  candidates.sort((a, b) => b.score - a.score);
  const claimedIdx = new Set();
  const claimedStore = new Set();
  const result = [];
  for (const c of candidates) {
    if (claimedIdx.has(c.idx) || claimedStore.has(c.store)) continue;
    claimedIdx.add(c.idx);
    claimedStore.add(c.store);
    result.push(c);
  }
  return result;
}

// Renders the yellow "Did you mean…?" cards above the preview table.
// acceptFnName / dismissFnName are global function names that need to
// be reachable from inline onclick handlers — e.g. "Uploads.budget.acceptSuggestion".
// Returns an HTML string ready to inject.
export function renderStoreSuggestionCards(suggestions, acceptFnName, dismissFnName) {
  if (!suggestions || !suggestions.length) return '';
  let html = '<div style="background:var(--adm-warn-pale);border:1px solid var(--adm-warn-line);border-radius:8px;padding:10px 12px;margin-bottom:10px"><strong style="color:var(--warn-deep);font-size:13px">💡 Did you mean…?</strong><div style="margin-top:4px;color:var(--warn-deep);font-size:11px;margin-bottom:4px">Some columns in your file look like missing stores. Confirm or skip each:</div>';
  suggestions.forEach(s => {
    const conf = s.score >= 0.95 ? 'very likely' : s.score >= 0.85 ? 'likely' : 'possibly';
    const confColor = s.score >= 0.95 ? 'var(--ok-deep)' : s.score >= 0.85 ? 'var(--warn-deep)' : 'var(--warn-deep)';
    const storeArg  = JSON.stringify(s.store);
    const headerArg = JSON.stringify(s.header);
    html += '<div style="display:flex;align-items:center;gap:8px;margin-top:6px;padding:8px 10px;background:var(--card);border-radius:6px;flex-wrap:wrap">';
    html += '<div style="flex:1;min-width:220px;font-size:12px"><strong>' + escapeHtml(s.store) + '</strong>';
    html += '<span style="color:var(--sub)"> → column </span>';
    html += '<code style="background:var(--surface-3);padding:1px 5px;border-radius:3px;font-size:11px">' + escapeHtml(s.header) + '</code> ';
    html += '<span style="color:' + confColor + ';font-size:11px;font-weight:500">(' + conf + ')</span></div>';
    html += '<div style="display:flex;gap:6px;flex-shrink:0">';
    html += '<button class="btn btn-sm btn-green" style="padding:3px 10px;font-size:11px" onclick="' + acceptFnName + '(' + storeArg + ',' + s.idx + ',' + headerArg + ')">Use this</button>';
    html += '<button class="btn btn-sm btn-outline" style="padding:3px 10px;font-size:11px" onclick="' + dismissFnName + '(' + storeArg + ')">Skip</button>';
    html += '</div></div>';
  });
  html += '</div>';
  return html;
}
